/**
 * Points Map — indexable poster first, Leaflet only near the viewport.
 */

import { loadLeaflet, createBaseLayers } from './leafletCommon.js';

const EXPAND_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/></svg>';
const SHRINK_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M4 14h6v6M14 4h6v6M10 14l-7 7M21 3l-7 7"/></svg>';

function fixMarkerIcons(L) {
  delete L.Icon.Default.prototype._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconUrl: new URL('leaflet/dist/images/marker-icon.png', import.meta.url).href,
    iconRetinaUrl: new URL('leaflet/dist/images/marker-icon-2x.png', import.meta.url).href,
    shadowUrl: new URL('leaflet/dist/images/marker-shadow.png', import.meta.url).href,
  });
}

function createFullscreenBtn(shell, container, map, L) {
  let btn;

  function setLabel(entering) {
    const label = entering
      ? container.dataset.mapExitFullscreenLabel
      : container.dataset.mapFullscreenLabel;
    btn.title = label;
    btn.setAttribute('aria-label', label);
  }

  function toggle() {
    const entering = !shell.classList.contains('is-fullscreen');
    shell.classList.toggle('is-fullscreen');
    btn.innerHTML = entering ? SHRINK_SVG : EXPAND_SVG;
    setLabel(entering);
    document.body.style.overflow = entering ? 'hidden' : '';
    map.scrollWheelZoom[entering ? 'enable' : 'disable']();
    requestAnimationFrame(() => map.invalidateSize());
  }

  const FullscreenControl = L.Control.extend({
    options: { position: 'topleft' },
    onAdd() {
      const wrap = L.DomUtil.create('div', 'leaflet-bar leaflet-control');
      btn = L.DomUtil.create('a', 'points-map-fullscreen-btn', wrap);
      btn.href = '#';
      btn.setAttribute('role', 'button');
      btn.innerHTML = EXPAND_SVG;
      setLabel(false);
      L.DomEvent.on(btn, 'click', L.DomEvent.stop).on(btn, 'click', toggle);
      return wrap;
    },
  });

  map.addControl(new FullscreenControl());

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && shell.classList.contains('is-fullscreen')) {
      toggle();
    }
  });

  return toggle;
}

function popupContent(point) {
  if (!point.href) {
    return document.createTextNode(point.label);
  }

  const link = document.createElement('a');
  link.href = point.href;
  link.textContent = point.label;
  link.style.fontWeight = '600';

  return link;
}

async function activateMap(container) {
  if (container.dataset.pmInit) return;
  container.dataset.pmInit = '1';

  const shell = container.closest('[data-points-map-shell]');
  const activateButton = shell.querySelector('[data-points-map-activate]');
  const status = shell.querySelector('[data-points-map-status]');
  activateButton.hidden = true;
  status.textContent = container.dataset.mapLoadingLabel;
  container.setAttribute('aria-busy', 'true');
  shell.classList.add('is-map-loading');

  try {
    const points = JSON.parse(container.dataset.points);
    if (!Array.isArray(points) || !points.length) {
      throw new Error('No map point');
    }

    const L = await loadLeaflet();
    fixMarkerIcons(L);

    const captureMode = document.documentElement.classList.contains('points-map-capture');
    const map = L.map(container, {
      scrollWheelZoom: false,
      attributionControl: !captureMode,
    });

    let mapTracked = false;
    map.on('dragstart popupopen', () => {
      if (mapTracked) return;
      mapTracked = true;
      window._paq?.push(['trackEvent', 'Engagement', 'Map view', location.pathname]);
    });

    const { baseLayers, defaultLayer } = createBaseLayers(L, container.dataset.ignKey || '');
    let revealed = false;
    const revealMap = () => {
      if (revealed) return;
      revealed = true;
      shell.classList.remove('is-map-loading');
      shell.classList.add('is-map-ready');
      container.setAttribute('aria-busy', 'false');
      status.textContent = '';
    };
    const finishCapture = () => {
      if (captureMode) setTimeout(() => window.stop(), 250);
    };
    defaultLayer.once('load', () => {
      revealMap();
      finishCapture();
    });
    defaultLayer.addTo(map);
    L.control.layers(baseLayers, null, { position: 'bottomleft' }).addTo(map);

    const exitFullscreen = createFullscreenBtn(shell, container, map, L);
    const bounds = [];
    const markersByHref = new Map();

    points.forEach((point) => {
      const latlng = [point.lat, point.lon];
      bounds.push(latlng);

      const marker = L.marker(latlng).bindPopup(popupContent(point)).addTo(map);
      if (point.href) markersByHref.set(point.href, marker);
    });

    container.addEventListener('click', (event) => {
      const link = event.target.closest('.leaflet-popup-content a[href]');
      if (!link) return;
      const href = link.getAttribute('href');
      if (href.startsWith('#')) {
        const target = document.getElementById(href.slice(1));
        if (!target) return;
        event.preventDefault();
        map.closePopup();
        if (shell.classList.contains('is-fullscreen')) exitFullscreen();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        map.closePopup();
        if (shell.classList.contains('is-fullscreen')) exitFullscreen();
      }
    });

    map.fitBounds(bounds, {
      paddingTopLeft: [140, 70],
      paddingBottomRight: [140, 70],
      maxZoom: 13,
    });

    container.applyMarkerFilter = (visibleHrefs) => {
      const shown = [];
      markersByHref.forEach((marker, href) => {
        if (!visibleHrefs || visibleHrefs.has(href)) {
          if (!map.hasLayer(marker)) marker.addTo(map);
          shown.push(marker.getLatLng());
        } else if (map.hasLayer(marker)) {
          map.removeLayer(marker);
        }
      });
      if (shown.length) map.fitBounds(shown, { padding: [70, 70], maxZoom: 12 });
    };
    if (container._pendingMarkerFilter !== undefined) {
      container.applyMarkerFilter(container._pendingMarkerFilter);
    }

    requestAnimationFrame(() => map.invalidateSize());

    if (captureMode) {
      setTimeout(() => {
        if (!revealed) {
          const tiles = container.querySelectorAll('.leaflet-tile');
          const loadedTiles = container.querySelectorAll('.leaflet-tile-loaded').length;
          if (tiles.length >= 4 && loadedTiles >= Math.ceil(tiles.length * 0.8)) {
            revealMap();
          } else {
            shell.classList.add('is-map-failed');
          }
        }
        window.stop();
      }, 8000);
    }
  } catch (error) {
    console.error('Points map activation failed', error);
    shell.classList.remove('is-map-loading');
    shell.classList.add('is-map-failed');
    container.setAttribute('aria-busy', 'false');
    status.textContent = container.dataset.mapFailedLabel;
  }
}

function initCaptureMode(shells, key) {
  const target = shells.find((shell) => shell.dataset.mapKey === key);
  if (!target) return false;

  document.documentElement.classList.add('points-map-capture');
  document.body.replaceChildren(target);
  activateMap(target.querySelector('.points-map'));

  return true;
}

export function initPointsMaps() {
  const shells = Array.from(document.querySelectorAll('[data-points-map-shell]'));
  const captureKey = new URLSearchParams(location.search).get('__points_map_capture');
  if (captureKey && initCaptureMode(shells, captureKey)) return;

  const saveData = navigator.connection?.saveData === true;
  const observer = 'IntersectionObserver' in window && !saveData
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          activateMap(entry.target.querySelector('.points-map'));
        });
      }, { rootMargin: '250px 0px' })
    : null;

  shells.forEach((shell) => {
    const container = shell.querySelector('.points-map[data-points]');
    const activateButton = shell.querySelector('[data-points-map-activate]');
    if (!container || container.dataset.pmQueued) return;
    container.dataset.pmQueued = '1';

    activateButton.hidden = false;
    activateButton.addEventListener('click', () => activateMap(container), { once: true });

    if (observer) {
      observer.observe(shell);
    } else if (!saveData) {
      activateMap(container);
    }
  });
}
